import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  Currency, 
  CurrencyCreateDto, 
  CurrencyUpdateDto,
  BulkCurrencyCreateDto,
  CurrencyQueryParams
} from "../../modules/shared/model";
import { 
  createCurrency as createCurrencyAction, 
  bulkCreateCurrencies as bulkCreateCurrenciesAction,
  updateCurrency as updateCurrencyAction, 
  getCurrencyById as getCurrencyByIdAction, 
  getCurrencies as getCurrenciesAction, 
  deleteCurrency as deleteCurrencyAction
} from "../Actions/CurrencyActions";

// Create Currency
export const createCurrency = createAsyncThunk("currencies/create", async (data: CurrencyCreateDto, { rejectWithValue }) => {
  try { return await createCurrencyAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create currency"); }
});

// Bulk Create Currencies
export const bulkCreateCurrencies = createAsyncThunk("currencies/bulkCreate", async (data: BulkCurrencyCreateDto, { rejectWithValue }) => {
  try { return await bulkCreateCurrenciesAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to bulk create currencies"); }
});

// Update Currency
export const updateCurrency = createAsyncThunk("currencies/update", async ({ id, data }: { id: string; data: CurrencyUpdateDto }, { rejectWithValue }) => {
  try { return await updateCurrencyAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update currency"); }
});

// Get Currency by ID
export const fetchCurrency = createAsyncThunk("currencies/getById", async (id: string, { rejectWithValue }) => {
  try { return await getCurrencyByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch currency"); }
});

// Get All Currencies
export const fetchCurrencies = createAsyncThunk("currencies/get", async (query: CurrencyQueryParams | undefined, { rejectWithValue }) => {
  try { return await getCurrenciesAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch currencies"); }
});

// Delete Currency
export const deleteCurrency = createAsyncThunk("currencies/delete", async (id: string, { rejectWithValue }) => {
  try { await deleteCurrencyAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete currency"); }
});

interface CurrenciesState {
  items: Currency[];
  current: Currency | null;
  loading: boolean;
  error: string | null;
  totalCount?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  page?: number;
  limit?: number;
}

const initialState: CurrenciesState = {
  items: [],
  current: null,
  loading: false,
  error: null,
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  page: 1,
  limit: 10,
};

const currenciesSlice = createSlice({
  name: "currencies",
  initialState,
  reducers: {
    clearCurrenciesError(state) { state.error = null; },
    clearCurrentCurrency(state) { state.current = null; },
    setCurrentCurrency(state, action: PayloadAction<Currency | null>) { 
      state.current = action.payload; 
    },
  },
  extraReducers: (builder) => {
    // Create Currency
    builder
      .addCase(createCurrency.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(createCurrency.fulfilled, (s, a: PayloadAction<Currency>) => { 
        s.loading = false; 
        s.items.unshift(a.payload); 
        s.totalCount = (s.totalCount || 0) + 1; 
      })
      .addCase(createCurrency.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Bulk Create Currencies
    builder
      .addCase(bulkCreateCurrencies.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(bulkCreateCurrencies.fulfilled, (s, a: PayloadAction<Currency[]>) => { 
        s.loading = false; 
        s.items.unshift(...a.payload); 
        s.totalCount = (s.totalCount || 0) + a.payload.length; 
      })
      .addCase(bulkCreateCurrencies.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Update Currency
    builder
      .addCase(updateCurrency.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(updateCurrency.fulfilled, (s, a: PayloadAction<Currency>) => {
        s.loading = false;
        const i = s.items.findIndex(x => x.id === a.payload.id);
        if (i !== -1) s.items[i] = a.payload;
        if (s.current?.id === a.payload.id) s.current = a.payload;
      })
      .addCase(updateCurrency.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Get Currency by ID
    builder
      .addCase(fetchCurrency.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchCurrency.fulfilled, (s, a: PayloadAction<Currency>) => { s.loading = false; s.current = a.payload; })
      .addCase(fetchCurrency.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Get All Currencies
    builder
      .addCase(fetchCurrencies.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchCurrencies.fulfilled, (s, a: PayloadAction<any>) => { 
        s.loading = false; 
        s.items = a.payload.data || []; 
        s.totalCount = a.payload.totalCount || 0;
        s.hasNextPage = a.payload.hasNextPage || false;
        s.hasPreviousPage = a.payload.hasPreviousPage || false;
        s.page = a.payload.page || 1;
        s.limit = a.payload.limit || 10;
      })
      .addCase(fetchCurrencies.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Delete Currency
    builder
      .addCase(deleteCurrency.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(deleteCurrency.fulfilled, (s, a: PayloadAction<string>) => { 
        s.loading = false; 
        s.items = s.items.filter(x => x.id !== a.payload); 
        if (s.current?.id === a.payload) s.current = null; 
        s.totalCount = Math.max(0, (s.totalCount || 0) - 1); 
      })
      .addCase(deleteCurrency.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });
  }
});

export const { 
  clearCurrenciesError, 
  clearCurrentCurrency,
  setCurrentCurrency
} = currenciesSlice.actions;
export default currenciesSlice.reducer;